import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Logo } from "@/components/site/Header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/admin/login");
    } catch (err) {
      const msg = apiErr(err.response?.data?.detail) || "Reset failed";
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 grain-band opacity-30" />
      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo light /></div>
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-11 w-11 rounded-xl bg-emerald-brand/10 text-emerald-brand flex items-center justify-center"><KeyRound size={20} /></span>
            <div><h1 className="font-display font-semibold text-xl text-navy">Set New Password</h1><p className="text-sm text-slate-500">Choose a strong new password</p></div>
          </div>
          {!token ? (
            <p data-testid="reset-notoken" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">This reset link is invalid or incomplete. Please request a new one.</p>
          ) : (
            <form onSubmit={submit} data-testid="reset-form" className="space-y-5">
              <div><Label>New Password</Label><Input required type="password" data-testid="reset-password" className="mt-1.5 focus-visible:ring-emerald-brand" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
              <div><Label>Confirm New Password</Label><Input required type="password" data-testid="reset-password2" className="mt-1.5 focus-visible:ring-emerald-brand" value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
              {error && <p data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full bg-emerald-brand hover:bg-emerald-dark rounded-full py-6 font-semibold">{loading ? "Updating…" : "Update Password"}</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
